import React from "react";

import { TextFieldProps } from "../../TextField";
import { DropdownMenuProps } from "../DropdownMenu";
import { DropdownOption } from "../types";
import { useKeyboardEvents } from "./useKeyboardEvents";

export function useDropdownMenu(props: {
  isSelect: boolean;
  dropdownOptions?: DropdownMenuProps["dropdownOptions"];
  onSelectOption?: TextFieldProps["onSelectOption"];
  onEnter?: () => void;
  onClickClear?: () => void;
  isFocused?: boolean;
}) {
  const {
    isSelect,
    dropdownOptions = [],
    onSelectOption = () => {},
    onEnter,
    isFocused
  } = props;
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Id for aria-controls
  const dropdownId = React.useId();

  const blurInput = React.useCallback(() => {
    inputRef.current?.blur();
  }, []);

  const onSelectCallback = React.useCallback(
    (option: DropdownOption) => {
      onSelectOption(option);
      blurInput();
    },
    [onSelectOption, blurInput]
  );

  const {
    focusedOptionIdx,
    handleKeyDown,
    iconButtonRef,
    handleCompositionStart,
    handleCompositionEnd
  } = useKeyboardEvents({
    dropdownOptions,
    onEnter,
    onSelectCallback,
    blurInput
  });

  const showDropdown = isSelect && isFocused;

  const handleClickOption = React.useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      const { relatedTarget } = event;
      if (relatedTarget && relatedTarget instanceof HTMLElement) {
        if (relatedTarget.getAttribute("role") === "option") {
          const { elementIdx } = relatedTarget.dataset;
          const selectedOption = dropdownOptions[Number(elementIdx)];
          if (selectedOption) {
            onSelectCallback(selectedOption);
          }
        }
      }
    },
    [dropdownOptions, onSelectCallback]
  );

  const ariaPropsForDropdown: Partial<React.HTMLAttributes<HTMLDivElement>> = {
    "aria-expanded": isSelect ? showDropdown : undefined,
    "aria-haspopup": isSelect ? "listbox" : false,
    "aria-controls": isSelect ? dropdownId : undefined,
    role: isSelect ? "combobox" : undefined
  };

  return {
    ariaPropsForDropdown,
    focusedOptionIdx,
    handleClickOption,
    handleKeyDown,
    iconButtonRef,
    inputRef,
    showDropdown,
    handleCompositionStart,
    handleCompositionEnd
  };
}
